import React from "react";
import {
  Box,
  Button,
  Typography,
  IconButton,
  TextField,
  Paper,
} from "@mui/material";

import UploadFileIcon from "@mui/icons-material/UploadFile";
import DescriptionIcon from "@mui/icons-material/Description";
import NotesIcon from "@mui/icons-material/Notes";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import GridOnIcon from "@mui/icons-material/GridOn";
import { styled } from "@mui/material/styles";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";

import { Trash2 } from "lucide-react";

/* ----------------------------------------
   LOCAL REUSABLE STYLES (SELF-CONTAINED)
---------------------------------------- */
const StyledPaper = styled(Paper)(({ theme }) => ({
  background: "linear-gradient(135deg, #ffffff 0%, #f8fafb 100%)",
  borderRadius: "20px",
  border: "1px solid rgba(0, 0, 0, 0.06)",
  boxShadow: "0 10px 40px rgba(0, 0, 0, 0.08)",
  padding: "48px",
  maxWidth: "1200px",
  margin: "0 auto",
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  "&:hover": {
    boxShadow: "0 15px 50px rgba(0, 0, 0, 0.12)",
  },
  [theme.breakpoints.down("md")]: {
    padding: "24px",
  },
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: "12px",
    backgroundColor: "#ffffff",
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    border: "2px solid rgba(0, 0, 0, 0.06)",
    fontSize: "15px",
    fontWeight: "500",
    color: "#1a1a1a",
    "& fieldset": {
      borderColor: "rgba(0, 0, 0, 0.06)",
    },
    "&:hover fieldset": {
      borderColor: "rgba(25, 118, 210, 0.3)",
    },
    "&.Mui-focused fieldset": {
      borderColor: "primary.main",
      borderWidth: "2px",
    },
    "&.Mui-focused": {
      boxShadow: "0 0 0 3px rgba(25, 118, 210, 0.1)",
      backgroundColor: "#ffffff",
    },
  },
  "& .MuiInputBase-input": {
    padding: "16px 16px",
    fontFamily: "'Inter', sans-serif",
    fontWeight: "500",
    "&::placeholder": {
      opacity: "0.6",
    },
  },
  "& .MuiInputLabel-root": {
    fontFamily: "'Inter', sans-serif",
    fontWeight: "600",
    color: "#4a5568",
    fontSize: "14px",
    transform: "translate(14px, -12px) scale(0.75)",
    backgroundColor: "#ffffff",
    paddingLeft: "6px",
    paddingRight: "6px",
    marginLeft: "-3px",
    "&.Mui-focused": {
      color: "primary.main",
    },
  },
  "& .MuiFormHelperText-root": {
    fontFamily: "'Inter', sans-serif",
    fontSize: "13px",
    marginLeft: "0",
    color: "#718096",
    marginTop: "8px",
  },
}));

const SectionTitle = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "12px",
  marginBottom: "24px",
  marginTop: "32px",
  paddingTop: "24px",
  borderTop: "2px solid rgba(0, 0, 0, 0.05)",
  "&:first-of-type": {
    marginTop: "0",
    borderTop: "none",
    paddingTop: "0",
  },
});

const IconWrapper = styled(Box)({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "28px",
  height: "28px",
  borderRadius: "8px",
  backgroundColor: "rgba(25, 118, 210, 0.1)",
  color: "primary.main",
  "& svg": {
    width: "16px",
    height: "16px",
  },
});

const SectionLabel = styled(Typography)({
  fontFamily: "'Inter', sans-serif",
  fontWeight: "700",
  fontSize: "14px",
  textTransform: "uppercase",
  letterSpacing: "1px",
  color: "primary.main",
});

const HeadingBox = styled(Box)({
  marginBottom: "40px",
  paddingBottom: "32px",
  borderBottom: "2px solid rgba(0, 0, 0, 0.05)",
});

const FormGrid = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "repeat(2, 1fr)",
  gap: "28px",
  marginBottom: "8px",
  [theme.breakpoints.down("md")]: {
    gridTemplateColumns: "1fr",
    gap: "20px",
  },
}));

const FullWidthGrid = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "repeat(2, 1fr)",
  gap: "28px",
  marginBottom: "8px",
  [theme.breakpoints.down("md")]: {
    gridTemplateColumns: "1fr",
    gap: "20px",
  },
}));

const FourColGrid = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "repeat(4, 1fr)",
  gap: "28px",
  marginBottom: "20px",
  [theme.breakpoints.down("lg")]: {
    gridTemplateColumns: "repeat(2, 1fr)",
  },
  [theme.breakpoints.down("sm")]: {
    gridTemplateColumns: "1fr",
  },
}));

const AssistanceBox = styled(Box)(({ theme }) => ({
  padding: "20px",
  borderRadius: "12px",
  background:
    "linear-gradient(135deg, rgba(25, 118, 210, 0.04) 0%, rgba(25, 118, 210, 0.02) 100%)",
  border: "1px solid rgba(25, 118, 210, 0.1)",
  marginBottom: "24px",
  marginTop: "24px",
  display: "flex",
  // alignItems: "center",
  flexDirection: "column",
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  "&:hover": {
    borderColor: "rgba(25, 118, 210, 0.2)",
    backgroundColor: "rgba(25, 118, 210, 0.06)",
  },
}));

const NotesBox = styled(Box)({
  marginBottom: "8px",
});

const defaultAddress = () => ({
  // Basic Contact
  firstName: "",
  lastName: "",
  companyName: "",
  email: "",
  phone: "",
  country: "",
  state: "",
  city: "",
  pincode: "",
  address1: "",
  address2: "",
  gst: "",
  landmark: "",

  // Pickup / Delivery extra fields
  locationType: "",
  assistanceAvailable: false,
  truckType: "",
  loadingEquipment: [],
  notes: "",

  // Vehicle Info
  vehicleNumber: "",
  driverName: "",
  driverPhone: "",

  // Pickup Docket
  pickupDate: "",
  pickupTime: "",
  confirmationNumber: "",
  carrier: "",
  conNote: "",

  // Uploads
  images: [],
  documents: {
    deliveryNote: [],
    packingList: [],
    weightSlip: [],
    invoice: [],
    consignmentNote: [],
    manifest: [],
  },

  trackingDetails: "",
});

/* ----------------------------------------
   REUSABLE UPLOAD SECTION
---------------------------------------- */
const UploadSection = ({
  label,
  type,
  documents,
  handleFileUpload,
  onDelete,
}) => {
  const file = documents[type]?.[0] || null;

  return (
    <Box>
      {/* STYLED TEXTFIELD UPLOADER */}
      <StyledTextField
        fullWidth
        label={label}
        value={documents[type]?.[0]?.name || ""} // Safely access the file name
        placeholder={`Upload ${label}`}
        InputLabelProps={{ shrink: true }} // ← forces the label to stay up
        InputProps={{
          readOnly: true,
          endAdornment: (
            <Button
              variant="contained"
              component="label"
              sx={{ ml: 1, px: 8, py: 1.5, borderRadius: "8px" }}
            >
              Upload
              <input type="file" hidden onChange={handleFileUpload(type)} />
            </Button>
          ),
        }}
      />
    </Box>
  );
};

/* ----------------------------------------
   MAIN FINAL COMPONENT
---------------------------------------- */
const TransportDocumentsForm = ({
  documents = defaultAddress().documents,
  trackingDetails = defaultAddress().trackingDetails,
  setTransport,
  errors,
}) => {
  const handleDelete = (type, index) => {
    setTransport((prev) => ({
      ...prev,
      documents: {
        ...prev.documents,
        [type]: prev.documents[type].filter((_, i) => i !== index),
      },
    }));
  };

  const handleTransportDocsUpload = (type) => (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Check if the selected file is a PDF
    if (file.type !== "application/pdf") {
      alert("Please upload a valid PDF file.");
      return;
    }

    // Update the state correctly, adding the new file
    setTransport((prev) => ({
      ...prev,
      documents: {
        ...prev.documents,
        [type]: [file], // Overwrite — only 1 file allowed
      },
    }));
  };

  return (
    <>
      <StyledPaper elevation={0}>
        <HeadingBox>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 800,
              fontSize: "30px",
              color: "#0a0e27",
              background: "linear-gradient(135deg, #0a0e27 0%, #1976d2 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              mb: 1,
            }}
          >
            Transport & Delivery Documents
          </Typography>

          <Typography sx={{ color: "#718096", fontSize: "15px" }}>
            Upload the required documents for third-party delivery
          </Typography>
        </HeadingBox>

        {/* DOCUMENT SECTIONS */}
        <SectionTitle>
          <IconWrapper>
            <DescriptionIcon />
          </IconWrapper>
          <SectionLabel>Documents</SectionLabel>
        </SectionTitle>

        <FormGrid>
          <UploadSection
            label="Delivery Note"
            type="deliveryNote"
            documents={documents}
            handleFileUpload={handleTransportDocsUpload}
            onDelete={handleDelete}
          />

          <UploadSection
            label="Packing List"
            type="packingList"
            documents={documents}
            handleFileUpload={handleTransportDocsUpload}
            onDelete={handleDelete}
          />

          <UploadSection
            label="Weight Slip"
            type="weightSlip"
            documents={documents}
            handleFileUpload={handleTransportDocsUpload}
            onDelete={handleDelete}
          />

          <UploadSection
            label="Invoice"
            type="invoice"
            documents={documents}
            handleFileUpload={handleTransportDocsUpload}
            onDelete={handleDelete}
          />

          <UploadSection
            label="Consignment Note"
            type="consignmentNote"
            documents={documents}
            handleFileUpload={handleTransportDocsUpload}
            onDelete={handleDelete}
          />

          <UploadSection
            label="Manifest"
            type="manifest"
            documents={documents}
            handleFileUpload={handleTransportDocsUpload}
            onDelete={handleDelete}
          />

          <Typography sx={{ fontSize: "12px", color: "#6c757d" }}>
            Only PDF files are accepted.
          </Typography>
        </FormGrid>

        {/* TRACKING DETAILS */}
        {/* NOTES */}
        <SectionTitle>
          <IconWrapper>
            <NotesIcon />
          </IconWrapper>
          <SectionLabel>Tracking Details</SectionLabel>
        </SectionTitle>
        <NotesBox>
          <Box
            sx={{
              padding: "16px",
              borderRadius: "12px",
              backgroundColor: "rgba(255,193,7,0.08)",
              border: "1px solid rgba(255,193,7,0.3)",
              display: "flex",
              gap: "12px",
              alignItems: "flex-start",
              mb: 5,
              mt: 2,
            }}
          >
            {/* ICON BOX */}
            <Box
              sx={{
                width: "28px",
                height: "28px",
                borderRadius: "6px",
                backgroundColor: "rgba(255,193,7,0.25)",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                mt: "2px",
              }}
            >
              <WarningAmberIcon sx={{ fontSize: 20, color: "#b7791f" }} />
            </Box>

            {/* TEXT CONTENT */}
            <Box sx={{ width: "100%" }}>
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: "15px",
                  mb: 1,
                  color: "#8a6d3b",
                }}
              >
                Tracking Details — What to include
              </Typography>

              {/* 2 COLUMN BULLET GRID */}
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr 1fr" },
                  rowGap: "8px",
                  columnGap: "16px",
                }}
              >
                <Typography sx={{ fontSize: "14px", color: "#6c757d" }}>
                  • Transporter name & contact number
                </Typography>

                <Typography sx={{ fontSize: "14px", color: "#6c757d" }}>
                  • Vehicle / container number
                </Typography>

                <Typography sx={{ fontSize: "14px", color: "#6c757d" }}>
                  • Driver name & phone number
                </Typography>

                <Typography sx={{ fontSize: "14px", color: "#6c757d" }}>
                  • Dispatch time & expected delivery time
                </Typography>

                <Typography sx={{ fontSize: "14px", color: "#6c757d" }}>
                  • Tracking link or LR / reference number
                </Typography>

                <Typography sx={{ fontSize: "14px", color: "#6c757d" }}>
                  • Latest shipment status or movement updates
                </Typography>
              </Box>
            </Box>
          </Box>
          <StyledTextField
            fullWidth
            label="Tracking Details*"
            multiline
            minRows={7}
            value={trackingDetails || ""}
            error={!!errors.trackingDetails} 
            helperText={
              errors.trackingDetails ?
              "Write any instructions that help the driver during delivery.": errors.trackingDetails
            }
            onChange={(e) =>
              setTransport((prev) => ({
                ...prev,
                trackingDetails: e.target.value,
              }))
            }
          />
        </NotesBox>
      </StyledPaper>
    </>
  );
};

export default TransportDocumentsForm;
